import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { FileDownload, Undo } from '@mui/icons-material';
import adminService from '../../services/adminService';
import { ButtonPill } from '../ui';
import { formatCurrency } from '../../utils/currency';

const PaymentsManagement = () => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refunding, setRefunding] = useState(false);

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await adminService.getAllPayments({ page: page + 1, per_page: rowsPerPage });
      const list = res?.payments ?? res?.data?.payments ?? [];
      setPayments(list);
      setTotalCount(res?.pagination?.total_count ?? res?.pagination?.total ?? list.length);
    } catch (err) {
      setError(err?.message || 'Failed to load payments');
      setPayments([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  const openRefund = (payment) => {
    setRefundTarget(payment);
    setRefundAmount(String(payment.amount ?? ''));
  };

  const submitRefund = async () => {
    if (!refundTarget) return;
    try {
      setRefunding(true);
      const res = await adminService.refundPayment(refundTarget.id, refundAmount);
      setRefundTarget(null);
      setSnackbar({ open: true, message: res?.message ?? 'Refund processed', severity: 'success' });
      fetchPayments();
    } catch (err) {
      setSnackbar({ open: true, message: err?.message ?? 'Refund failed', severity: 'error' });
    } finally {
      setRefunding(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await adminService.exportCsv('payments');
      setSnackbar({ open: true, message: 'Export started', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err?.message ?? 'Export failed', severity: 'error' });
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
      case 'processing':
        return 'warning';
      case 'failed':
      case 'refunded':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading && payments.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Payments & Transactions
        </Typography>
        <ButtonPill
          size="small"
          variant="outlined"
          startIcon={<FileDownload />}
          disabled={exporting}
          onClick={handleExport}
        >
          Export CSV
        </ButtonPill>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
              <TableCell>Transaction ID</TableCell>
              <TableCell>Booking Reference</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Gateway</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No payments found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              payments.map((payment) => (
                <TableRow key={payment.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {payment.transaction_id || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {payment.booking?.booking_reference || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(payment.amount, payment.currency)}
                    </Typography>
                    {payment.refund_amount > 0 && (
                      <Typography variant="caption" color="error">
                        refunded {formatCurrency(payment.refund_amount, payment.currency)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={payment.payment_method} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip label={payment.payment_gateway || payment.payment_method} size="small" color="primary" />
                  </TableCell>
                  <TableCell>
                    {new Date(payment.paid_at || payment.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={payment.status}
                      color={getStatusColor(payment.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {payment.status === 'completed' ? (
                      <Button size="small" color="error" startIcon={<Undo />} onClick={() => openRefund(payment)} sx={{ textTransform: 'none' }}>
                        Refund
                      </Button>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </TableContainer>

      <Dialog open={Boolean(refundTarget)} onClose={() => !refunding && setRefundTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Refund payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {refundTarget?.booking?.booking_reference ? `Booking ${refundTarget.booking.booking_reference}. ` : ''}
            Original amount {formatCurrency(refundTarget?.amount, refundTarget?.currency)}. Enter the amount to refund (defaults to full).
          </Typography>
          <TextField
            label="Refund amount"
            type="number"
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
            fullWidth
            inputProps={{ min: 0, max: refundTarget?.amount, step: '0.01' }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRefundTarget(null)} disabled={refunding} sx={{ borderRadius: 9999 }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={submitRefund}
            disabled={refunding || !(parseFloat(refundAmount) > 0)}
            startIcon={refunding ? <CircularProgress size={16} color="inherit" /> : <Undo />}
            sx={{ borderRadius: 9999 }}
          >
            {refunding ? 'Processing…' : 'Process refund'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentsManagement;


